import { BfiApi, IApiResponse } from './bfiApi';
import { NoticeType, NoticeParentType, UserNoticeSubSectionType, NoticeStatusType, LoanType } from '@apply/constants';
import { ISearchCriteria } from './common';
import { ESignType } from 'libs/store/src/user/types';
import { IAddress } from 'libs/store/src/cross';

export interface IPaging {
    skip: number;
    take: number;
}

export interface INoticesSearchRequest extends ISearchCriteria {
    subSectionId: UserNoticeSubSectionType;
}

export interface INoticeAddress extends IAddress {
    id: number;
    enabled: boolean;
}

export interface IUserNoticeSummary {
    id: number;
    isManual: boolean;
    noticeTypeId: NoticeType;
    noticeDate: Date;
    isRead: boolean;
    eSignDate?: Date | null;
    eSignName: string;
    esignType: ESignType;
    noticeFile: INoticeFile;
    signedFile: INoticeFile;
    addresses: [INoticeAddress];
    sentToEmail: string;
    sentToName: string;
    sentByName: string;
    statusId: NoticeStatusType;
    error: string;
    canEmail: boolean;
    canDelete: boolean;
}

export interface INoticeGridResult {
    borrowerEntity: string;
    propertyStreet1: string;
    propertyStreet2: string;
    propertyCity: string;
    propertyState: string;
    propertyZip: string;
    bfiNumber: string;
    createdAt: Date;
    daysSinceApplied: number;
    poc: string;
    noticesToName: string;
    noticesToEmail: string;
    noticesToEsign: Date;
}

export interface INoticeFile {
    id: number;
    tempFileName: string;
}

export interface IManualNoticeIn {
    parentTypeId: NoticeParentType;
    parentId: number;
    noticeTypeId: NoticeType;
}

export interface IManualNoticeOut extends IManualNoticeIn {
    status: string;
    loanTypeId: LoanType;
    SentByUserId: string;
    NoticeDate: Date;
    TempFileName: string;
    address: INoticeAddress;
    incompleteReasonTypeIds: [number];
    companyTaxReturnAdditionalInfo: string;
    personalTaxReturnAdditionalInfo: string;
    declineReasonTypeIds: [number];
    declineReasonOther: string;
    declineReasonUnableToVerifyIdentity: string;
    canEmail: boolean;
    selectRecipient: boolean;
    selectedRecipientUserId?: number;
}

export interface IUserNoticesResponse extends Omit<IApiResponse, 'result'> {
    result: [IUserNoticeSummary];
}

export interface INoticeGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [INoticeGridResult];
    };
}

export interface IManualNoticesOutResponse extends Omit<IApiResponse, 'result'> {
    result: IManualNoticeOut;
}

export interface IPostClosingCertRecipientListResponse extends Omit<IApiResponse, 'result'> {
    result: IPostClosingCertRecipientList[];
}

export interface IPostClosingCertRecipientList {
    principalId: number;
    entityName: string;
    name: string;
    percentOwnership: number;
}

export interface IPostClosingCertNoticeQueueItem {
    principalId: number;
    loanId: number;
    propertyId: number;
}

export class NoticesApi extends BfiApi {
    getUserNotices(request: IPaging): Promise<IUserNoticesResponse> {
        return super.post(`/account/notices/`, request);
    }

    getGrid(request: INoticesSearchRequest): Promise<INoticeGridResponse> {
        return super.post(`/notices/grid`, request);
    }

    getNoticeById(id: number): Promise<IUserNoticesResponse> {
        return super.get(`/notices/${id}`);
    }

    updateNoticeFile(model: INoticeFile): Promise<IApiResponse> {
        return super.post(`/notices/updatefile`, model);
    }

    uploadSignedNoticeFile(noticeId: number, file): Promise<IApiResponse> {
        const formData = new FormData();
        formData.append('file', file);
        return super.post(`/notices/${noticeId}/signedFile`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    }

    getAddNotice(model: IManualNoticeIn): Promise<IManualNoticesOutResponse> {
        return super.post(`/notices/manualnotice/get`, model);
    }

    setAddNotice(model: IManualNoticeOut): Promise<IApiResponse> {
        return super.post(`/notices/manualnotice/add`, model);
    }

    requeueNotice(noticeId: number): Promise<IApiResponse> {
        return super.get(`/notices/${noticeId}/requeue`);
    }

    emailNotice(noticeId: number): Promise<IApiResponse> {
        return super.get(`/notices/${noticeId}/email`);
    }

    getPostClosingCertSendingAvailability(loanPropertyId: number): Promise<boolean> {
        return super.get(`/notices/postClosingCertEnabled/loanProperty/${loanPropertyId}/get`).then((r) => {
            return r.result === true;
        });
    }

    getPostClosingCertRecipientListByLoan(loanId: number): Promise<IPostClosingCertRecipientListResponse> {
        return super.get(`/notices/postClosingCertRecipients/loan/${loanId}/get`);
    }

    getPostClosingCertRecipientListByProperty(loanPropertyId: number): Promise<IPostClosingCertRecipientListResponse> {
        return super.get(`/notices/postClosingCertRecipients/loanProperty/${loanPropertyId}/get`);
    }

    queuePostClosingCertNotice(model: IPostClosingCertNoticeQueueItem): Promise<IApiResponse> {
        return super.post(`/notices/queuePostClosingCertNotice`, model);
    }
}
